import { useGetIdentity, useLogout } from "@refinedev/core";
import type { ReactNode } from "react";
import { NavLink } from "react-router";

import { useConfig } from "../hooks/useConfig";

type Identity = { name: string; email: string; role: string };

const NAV: { to: string; label: string; group?: string; adminOnly?: boolean }[] = [
  { to: "/", label: "Dashboard" },
  { to: "/orders", label: "Orders" },
  { to: "/dispatch", label: "Dispatch" },
  { to: "/vendors", label: "Vendors" },
  { to: "/users", label: "Users" },
  { to: "/vendor-types", label: "Vendor types", group: "Catalogue", adminOnly: true },
  { to: "/categories", label: "Categories", group: "Catalogue", adminOnly: true },
  { to: "/banners", label: "Banners", group: "Catalogue", adminOnly: true },
  { to: "/coupons", label: "Coupons", group: "Catalogue", adminOnly: true },
  { to: "/package-types", label: "Package types", group: "Catalogue", adminOnly: true },
  { to: "/remittances", label: "Remittances", group: "Finance" },
  { to: "/earnings", label: "Earnings", group: "Finance", adminOnly: true },
  { to: "/payouts", label: "Payouts", group: "Finance", adminOnly: true },
  { to: "/payment-methods", label: "Payment methods", group: "Settings", adminOnly: true },
  { to: "/settings", label: "Settings", group: "Settings", adminOnly: true },
  { to: "/audit-log", label: "Audit log", group: "Settings", adminOnly: true },
  { to: "/subscription-plans", label: "Subscription plans", group: "Catalogue", adminOnly: true },
  { to: "/geo", label: "Countries & cities", group: "Catalogue", adminOnly: true },
  { to: "/broadcast", label: "Broadcast", group: "Settings", adminOnly: true },
  { to: "/imports", label: "Imports", group: "Settings", adminOnly: true },
  { to: "/translations", label: "Translations", group: "Settings", adminOnly: true },
];

export function Layout({ children }: { children: ReactNode }) {
  const { data: identity } = useGetIdentity<Identity>();
  const { mutate: logout } = useLogout();
  const config = useConfig();
  const isAdmin = identity?.role === "admin";

  let lastGroup: string | undefined;
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <span>{config?.app_name ?? "Admin"}</span>
        </div>
        <nav>
          {NAV.filter((n) => isAdmin || !n.adminOnly).map((n) => {
            const head = n.group !== lastGroup ? n.group : undefined;
            lastGroup = n.group;
            return (
              <div key={n.to}>
                {head && <div className="nav-group">{head}</div>}
                <NavLink to={n.to} end={n.to === "/"} className={({ isActive }) => (isActive ? "active" : undefined)}>
                  {n.label}
                </NavLink>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {identity && (
            <div className="identity">
              <strong>{identity.name}</strong>
              <small>
                {identity.email} · {identity.role}
              </small>
            </div>
          )}
          <button type="button" className="btn btn-ghost" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </aside>
      <main className="content">{children}</main>
    </div>
  );
}
